import React, { useState, useEffect, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Tabs,
  Tab,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Sync as SyncIcon,
  Dashboard as DashboardIcon,
  Category as CategoryIcon,
  ShoppingCart as ProductIcon,
  Star as StarIcon,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TabPanel from "../../components/TabPanel";

import ProductList from "../../components/Catalog/ProductList";
import ProductForm from "../../components/Catalog/ProductForm";
import CategoryManager from "../../components/Catalog/CategoryManager";
import SyncStatus from "../../components/Catalog/SyncStatus";
import CatalogDashboard from "../../components/Catalog/CatalogDashboard";
import FeaturedProductsManager from "../../components/Catalog/FeaturedProductsManager";

import { AuthContext } from "../../context/Auth/AuthContext";
import PlanPermissionsContext from "../../context/PlanPermissions/PlanPermissionsContext";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import toastUtils from "../../utils/toastUtils";
import { useHistory } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    backgroundColor: theme.palette.background.paper,
  },
  mainPaper: {
    ...theme.scrollbarStyles,
    overflowY: "scroll",
    flex: 1,
  },
  tab: {
    backgroundColor: theme.mode === "light" ? "#f2f2f2" : "#7f7f7f",
    borderRadius: 4,
  },
  paper: {
    ...theme.scrollbarStyles,
    overflowY: "scroll",
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
  },
  statsCard: {
    textAlign: "center",
    padding: theme.spacing(2),
    background: `linear-gradient(135deg, ${theme.palette.primary.main}20, ${theme.palette.primary.main}10)`,
    border: `1px solid ${theme.palette.primary.main}30`,
  },
  statsNumber: {
    fontSize: "2rem",
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
  statsLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  syncButton: {
    marginLeft: theme.spacing(1),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: 200,
  },
}));

const Catalog = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { hasPermission, loading: permissionsLoading } = useContext(PlanPermissionsContext);
  const history = useHistory();

  const [tab, setTab] = useState("dashboard");
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [stats, setStats] = useState({
    totalProducts: 0,
    activeProducts: 0,
    totalCategories: 0,
    syncedProducts: 0,
    pendingSync: 0,
  });
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [featuredManagerOpen, setFeaturedManagerOpen] = useState(false);

  // Check plan permission
  useEffect(() => {
    if (!permissionsLoading && !hasPermission('useWhatsappCatalog')) {
      console.log('⛔ Recurso Catálogo não disponível no plano');
      toastError("Catálogo não disponível no seu plano atual");
      history.push("/");
    }
  }, [hasPermission, permissionsLoading, history]);

  // Load catalog statistics
  useEffect(() => {
    if (!permissionsLoading && hasPermission('useWhatsappCatalog')) {
      loadStats();
    }
  }, [refreshTrigger, hasPermission, permissionsLoading]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/catalog/stats");
      setStats(data);
    } catch (err) {
      console.error("Error loading catalog stats:", err);
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleSyncCatalog = async () => {
    try {
      setSyncLoading(true);
      await api.post("/catalog/sync");
      toastUtils.success("Sincronização iniciada com sucesso!");
      // Refresh stats after sync
      setTimeout(() => {
        loadStats();
      }, 2000);
    } catch (err) {
      console.error("Error syncing catalog:", err);
      toastError(err);
    } finally {
      setSyncLoading(false);
    }
  };

  const handleProductSave = () => {
    setProductModalOpen(false);
    setSelectedProduct(null);
    setRefreshTrigger(prev => prev + 1);
  };

  const handleProductEdit = (product) => {
    setSelectedProduct(product);
    setProductModalOpen(true);
  };

  const handleProductAdd = () => {
    setSelectedProduct(null);
    setProductModalOpen(true);
  };

  const renderStatsCards = () => (
    <Grid container spacing={3} style={{ marginBottom: 24 }}>
      <Grid item xs={12} sm={6} md={2.4}>
        <Card className={classes.statsCard}>
          <CardContent>
            <Typography className={classes.statsNumber}>
              {stats.totalProducts}
            </Typography>
            <Typography className={classes.statsLabel}>
              Total de Produtos
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={2.4}>
        <Card className={classes.statsCard}>
          <CardContent>
            <Typography className={classes.statsNumber}>
              {stats.activeProducts}
            </Typography>
            <Typography className={classes.statsLabel}>
              Produtos Ativos
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={2.4}>
        <Card className={classes.statsCard}>
          <CardContent>
            <Typography className={classes.statsNumber}>
              {stats.totalCategories}
            </Typography>
            <Typography className={classes.statsLabel}>
              Categorias
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={2.4}>
        <Card className={classes.statsCard}>
          <CardContent>
            <Typography className={classes.statsNumber}>
              {stats.syncedProducts}
            </Typography>
            <Typography className={classes.statsLabel}>
              Sincronizados
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={2.4}>
        <Card className={classes.statsCard}>
          <CardContent>
            <Typography className={classes.statsNumber}>
              {stats.pendingSync}
            </Typography>
            <Typography className={classes.statsLabel}>
              Pendentes
            </Typography>
            {stats.pendingSync > 0 && (
              <Chip
                size="small"
                color="secondary"
                label="Ação necessária"
                style={{ marginTop: 4 }}
              />
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  if (!user) {
    return null;
  }

  if (permissionsLoading) {
    return (
      <MainContainer>
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      </MainContainer>
    );
  }

  if (!hasPermission('useWhatsappCatalog')) {
    return null;
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>Catálogo WhatsApp Business</Title>
        <MainHeaderButtonsWrapper>
          <SyncStatus />
          <Tooltip title="Sincronizar com WhatsApp">
            <Button
              variant="outlined"
              color="primary"
              onClick={handleSyncCatalog}
              disabled={syncLoading}
              className={classes.syncButton}
              startIcon={syncLoading ? <CircularProgress size={20} /> : <SyncIcon />}
            >
              {syncLoading ? "Sincronizando..." : "Sincronizar"}
            </Button>
          </Tooltip>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setFeaturedManagerOpen(true)}
            startIcon={<StarIcon />}
            style={{ marginRight: 8 }}
          >
            Gerenciar Destaques
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleProductAdd}
            startIcon={<AddIcon />}
          >
            Novo Produto
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper}>
        <Box className={classes.paper}>
          {loading ? (
            <div className={classes.loadingContainer}>
              <CircularProgress />
            </div>
          ) : (
            <>
              {renderStatsCards()}
              
              <Tabs
                value={tab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab
                  label="Dashboard"
                  value="dashboard"
                  icon={<DashboardIcon />}
                  className={classes.tab}
                />
                <Tab
                  label="Produtos"
                  value="products"
                  icon={<ProductIcon />}
                  className={classes.tab}
                />
                <Tab
                  label="Categorias"
                  value="categories"
                  icon={<CategoryIcon />}
                  className={classes.tab}
                />
              </Tabs>

              <TabPanel value={tab} index="dashboard">
                <CatalogDashboard 
                  stats={stats}
                  onRefresh={() => setRefreshTrigger(prev => prev + 1)}
                />
              </TabPanel>

              <TabPanel value={tab} index="products">
                <ProductList
                  onEdit={handleProductEdit}
                  onRefresh={() => setRefreshTrigger(prev => prev + 1)}
                  refreshTrigger={refreshTrigger}
                />
              </TabPanel>

              <TabPanel value={tab} index="categories">
                <CategoryManager
                  onRefresh={() => setRefreshTrigger(prev => prev + 1)}
                  refreshTrigger={refreshTrigger}
                />
              </TabPanel>
            </>
          )}
        </Box>
      </Paper>

      {/* Product Form Modal */}
      <ProductForm
        open={productModalOpen}
        onClose={() => setProductModalOpen(false)}
        product={selectedProduct}
        onSave={handleProductSave}
      />

      {/* Featured Products Manager */}
      <FeaturedProductsManager
        open={featuredManagerOpen}
        onClose={() => setFeaturedManagerOpen(false)}
        onUpdate={() => setRefreshTrigger(prev => prev + 1)}
      />
    </MainContainer>
  );
};

export default Catalog;